/**
 * Safely converts a Firestore Timestamp, Date, string, or number to a JavaScript Date object.
 * Returns null if the input is null, undefined, or produces an invalid date.
 *
 * @param value - The value to convert (Firestore Timestamp, Date, string, number, or null/undefined)
 * @returns A valid Date object or null if conversion fails or input is null/undefined
 *
 * @example
 * ```ts
 * const date1 = toJSDate(firestoreTimestamp); // Date or null
 * const date2 = toJSDate("2024-01-01"); // Date
 * const date3 = toJSDate(null); // null
 * const date4 = toJSDate(undefined); // null
 * const date5 = toJSDate("invalid"); // null (invalid date)
 * ```
 */
export const toJSDate = (value: unknown): Date | null => {
  // Handle null/undefined
  if (value == null) {
    return null;
  }

  let date: Date;

  // Already a Date object
  if (value instanceof Date) {
    date = value;
  }
  // String or number
  else if (typeof value === "string" || typeof value === "number") {
    date = new Date(value);
  }
  // Firestore Timestamp (has toDate method)
  else if (
    typeof value === "object" &&
    "toDate" in value &&
    typeof value.toDate === "function"
  ) {
    date = value.toDate();
  }
  // Unsupported type
  else {
    return null;
  }

  // Validate the resulting date
  if (isNaN(date.getTime())) {
    return null;
  }

  return date;
};
